// OP-3605 — Tela do pedido para a logística (ver: ESL1; agir: ESL3). A fila
// lista; aqui se decide: transições de status, pedir informação, separação
// (quantidade e peça a peça), alteração de quantidade e expedição. Os gates
// da tela (regras.ts) espelham o domínio; quem recusa de verdade é o servidor.
import { FC, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  useEstoqueAnexos, useEstoqueDisponiveis, useEstoqueLocais, useEstoqueRemessasDoPedido, useEstoqueSolicitacaoDetalhe, useEstoqueVinculosDoDetentor,
} from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import {
  alterarQuantidadeEstoqueSolicitacao, createEstoqueAnexoUrl, createEstoqueVinculo, descartarEstoqueAnexo, desvincularEstoqueSeparacao,
  expedirEstoqueSolicitacao, pedirInformacaoEstoqueSolicitacao, redirecionarEstoqueSolicitacao, saveEstoqueAnexo, separarQuantidadeEstoqueSolicitacao,
  transicionarEstoqueSolicitacao, vincularEstoqueSeparacao,
} from '../../../../Service/estoque'
import {
  DESTINOS_SALDO, DestinoSaldo, METODOS_ENVIO, rotuloMetodoEnvio, MetodoEnvio, ROTULO_CAMPO, StatusRemessa, StatusSolicitacao, TIPO_DEMANDA_LABEL,
} from '../../../../types/estoque'
import { STATUS_REMESSA_UI } from '../Remessa/regras'
import { avisoDoVinculoNaExpedicao } from '../Vinculo/regras'
import { hojeISO } from '../Solicitar/form'
import {
  BD, SEVERIDADE, BotaoIcone, Button, Card, DetailLayout, Field, Folha, FolhaOverlay, FolhaPega, FolhaTopo, Input, LinkBotao, ListaCartoes, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { BarraFixa } from '../BarraFixa'
import { AcaoDecisao, Decisao } from '../Decisao'
import { AnexarArquivo } from '../AnexarArquivo'
import { exibirDocumento, mascaraDocumento } from '../documento'
import { STATUS_TRANSFERENCIA_UI } from '../Transferencia/regras'
import { ERRO_TEXTO, tratarErro } from '../erros'
import { SeletorDePecas } from '../SeletorDePecas'
import { numeroComDdi, prazoUi, textoRegime } from '../formato'
import { Stepper } from '../Stepper'
import { STATUS_SOLICITACAO_UI, ehFinal } from '../statusSolicitacao'
import {
  FORM_EXPEDICAO_VAZIO, FormExpedicao, IMPEDIMENTO_FOTO_SEPARACAO, LinhaCobertura, ROTULO_TRANSICAO, TOTAIS_VAZIO, acoesDoPedido, faltaFotoDaSeparacao, fotosPorRodada, rotaPedirTransferencia, avisoEquipamentoEmCampo, cancelamentoBloqueadoPorEntrega, cobertura, exigeMotivo, impedimentoConfirmarCancelamentoPedido,
  destinosDoRedirecionamento, impedimentoExpedicao, impedimentoRedirecionar, lerCampos, limiteSeparacao, mostrarBlocoRemessas, podeCancelarPedido, restante, rotuloDoMotivo, textoRestante, totais, transicoesOferecidas,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const NOTA_UI = { bg: '#F3F4F6', color: '#374151', label: 'Nota' }
const REDIRECIONAMENTO_UI = { bg: '#EDE9FE', color: '#5B21B6', label: 'Redirecionada' }

const LIMITE_PECAS = 50

const Bloco: FC<{ titulo: string; children: any; destaque?: boolean; id?: string }> = ({ titulo, children, destaque, id }) => (
  <Card id={id} style={{ padding: 16, marginBottom: 16, borderColor: destaque ? SEVERIDADE.atencao.borda : undefined }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>
    {children}
  </Card>
)

export const EstoquePedido: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const { pode, usuarioId } = usePerfilEstoque()
  const atende = pode('ATENDER')
  // A expedição usa rotas de OUTRAS alçadas: criar/renovar contrato é ESL11 e
  // o comprovante obrigatório sobe por duas rotas ESL18. Quem atende (ESL3)
  // pode não ter nenhuma das duas — a tela diz isso em vez de oferecer um
  // botão que responde NOT_PERMITED.
  const podeVincular = pode('VINCULOS')
  const podeAnexar = pode('ANEXAR')
  const podeTransferir = pode('TRANSFERIR')
  const { data: s, isLoading, isError, isUnauthorized, mutate } = useEstoqueSolicitacaoDetalhe(idNum)
  const [pagRemessas, setPagRemessas] = useState(1)
  const remessasQ = useEstoqueRemessasDoPedido(idNum, pagRemessas)
  const vinculosQ = useEstoqueVinculosDoDetentor(s?.detentor_local_id ?? null)
  const vinculos = vinculosQ.rows

  const [busy, setBusy] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [visivel, setVisivel] = useState(true)
  const [pergunta, setPergunta] = useState('')
  const [qtdSeparar, setQtdSeparar] = useState<Record<number, string>>({})
  const [qtdNova, setQtdNova] = useState<Record<number, string>>({})
  const [motivoQtd, setMotivoQtd] = useState('')
  const [pickerItem, setPickerItem] = useState<number | null>(null)
  const [buscaPeca, setBuscaPeca] = useState('')
  const [enviadoAgora, setEnviadoAgora] = useState<string | null>(null)
  const [exp, setExp] = useState<FormExpedicao>(FORM_EXPEDICAO_VAZIO)
  const [comprovanteNome, setComprovanteNome] = useState<string | null>(null)
  const [acoesAbertas, setAcoesAbertas] = useState(false)
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
  const [localDestinoRedir, setLocalDestinoRedir] = useState<number | null>(null)
  const [motivoRedir, setMotivoRedir] = useState('')
  const { locais } = useEstoqueLocais()

  const disponiveisQ = useEstoqueDisponiveis(pickerItem, s?.local_origem_id ?? null, LIMITE_PECAS, buscaPeca)
  // Comprovantes de despacho já gravados neste pedido: quem atende sem ESL18
  // aponta o que outra pessoa enviou, em vez de ficar sem poder expedir.
  const anexosQ = useEstoqueAnexos('SOLICITACAO', idNum, 'DESPACHO')
  const fotosQ = useEstoqueAnexos('SOLICITACAO', idNum, 'SEPARACAO')
  const comprovantesSalvos = anexosQ.data
  const [atualizando, setAtualizando] = useState(false)
  const hoje = useMemo(hojeISO, [])

  // Eventos da remessa: por padrão todos os do pedido; contrato vigente: o primeiro.
  useEffect(() => {
    if (s && exp.eventos.length === 0 && s.eventos.length) setExp((e) => ({ ...e, eventos: s.eventos.map((ev) => ev.eventoId) }))
  }, [s, exp.eventos.length])
  useEffect(() => {
    if (!exp.vinculoId && vinculos.length) setExp((e) => ({ ...e, vinculoId: vinculos[0].id }))
  }, [vinculos, exp.vinculoId])

  const linhas = useMemo(() => (s ? cobertura(s) : []), [s])
  const t = useMemo(() => (s ? totais(s) : TOTAIS_VAZIO), [s])

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !s) {
    return (
      <>
        <PageTitle>Pedido</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>Pedido não encontrado ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/fila')}>Voltar à fila</Button></div></Card>
      </>
    )
  }

  const executar = async (acao: () => Promise<any>, ok: string, limpar?: () => void) => {
    setBusy(true)
    try {
      await acao()
      toast.success(ok)
      if (limpar) limpar()
      // As remessas têm cache próprio e não revalidam por foco: sem isto, a
      // primeira expedição deixava o bloco "Remessas deste pedido" ausente
      // até alguém recarregar a página.
      await Promise.all([mutate(), remessasQ.mutate()])
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await Promise.all([mutate(), remessasQ.mutate()])
    } finally {
      setBusy(false)
    }
  }

  const ui = STATUS_SOLICITACAO_UI[s.status] ?? { bg: '#F3F4F6', color: '#374151', label: s.status }
  const campos = lerCampos(s.campos)
  const rest = restante(t)
  const temSaldo = rest.total > 0
  const cancelavel = podeCancelarPedido(s)
  const manuais = transicoesOferecidas(s.status, cancelavel)
  const emSeparacao = s.status === 'EM_SEPARACAO'
  const faltaFoto = faltaFotoDaSeparacao(s)
  const rodadaAberta = s.status === 'EM_SEPARACAO' || s.status === 'EM_ESPERA'
  const fotos = fotosPorRodada(fotosQ.data, s.separacao?.inicioDaRodada)
  const podeAlterarQtd = !ehFinal(s.status) && ['EXPEDIDA'].indexOf(s.status) < 0

  const transicionar = (para: StatusSolicitacao) => {
    if (para === 'PRONTA' && faltaFoto) { toast.error(IMPEDIMENTO_FOTO_SEPARACAO); return }
    if (exigeMotivo(s.status, para, temSaldo) && !motivo.trim()) { toast.error('Informe o motivo.'); return }
    return executar(
      () => transicionarEstoqueSolicitacao(s.id, para, motivo.trim() || undefined, visivel),
      `Pedido: ${STATUS_SOLICITACAO_UI[para]?.label ?? para}`,
      () => { setMotivo(''); setAcoesAbertas(false); setConfirmandoCancelar(false) },
    )
  }
  const fecharFolhaDeAcoes = () => { setAcoesAbertas(false); setConfirmandoCancelar(false) }
  const impConfirmarCancelamento = impedimentoConfirmarCancelamentoPedido(motivo)
  const armado = confirmandoCancelar && manuais.indexOf('CANCELADA') >= 0
  const bloqueioDoCancelamento = cancelamentoBloqueadoPorEntrega(s) ? ERRO_TEXTO.CANCELAMENTO_COM_ENTREGA_FEITA : null

  const trocarComprovante = async () => {
    const key = exp.comprovanteKey
    setExp((e) => ({ ...e, comprovanteKey: null }))
    setComprovanteNome(null)
    if (!key || key !== enviadoAgora) return
    setEnviadoAgora(null)
    setBusy(true)
    try {
      await descartarEstoqueAnexo('SOLICITACAO', s.id, key)
      await anexosQ.mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.info('O comprovante anterior continua nos anexos do pedido.')
    } finally {
      setBusy(false)
    }
  }

  const enviarComprovante = async (comprovanteFile: File) => {
    setBusy(true)
    try {
      const { data } = await createEstoqueAnexoUrl('SOLICITACAO', s.id, comprovanteFile.type)
      const put = await fetch(data.data.url, { method: 'PUT', headers: { 'Content-Type': comprovanteFile.type }, body: comprovanteFile })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      await saveEstoqueAnexo({ refTipo: 'SOLICITACAO', refId: s.id, escopo: 'DESPACHO', key: data.data.key, nome: comprovanteFile.name, mime: comprovanteFile.type, bytes: comprovanteFile.size })
      setExp((e) => ({ ...e, comprovanteKey: data.data.key }))
      setEnviadoAgora(data.data.key)
      setComprovanteNome(comprovanteFile.name)
      // A lista de já enviados serve a quem não pode anexar: mantém-se atual.
      await anexosQ.mutate()
      toast.success('Comprovante enviado')
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto === 'ERRO' ? 'Falha no upload do comprovante' : e.texto)
    } finally {
      setBusy(false)
    }
  }

  const enviarFotoSeparacao = async (foto: File) => {
    setBusy(true)
    try {
      const { data } = await createEstoqueAnexoUrl('SOLICITACAO', s.id, foto.type)
      const put = await fetch(data.data.url, { method: 'PUT', headers: { 'Content-Type': foto.type }, body: foto })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      await saveEstoqueAnexo({ refTipo: 'SOLICITACAO', refId: s.id, escopo: 'SEPARACAO', key: data.data.key, nome: foto.name, mime: foto.type, bytes: foto.size })
      await Promise.all([fotosQ.mutate(), mutate()])
      toast.success('Foto da separação enviada')
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto === 'ERRO' ? 'Falha no upload da foto' : e.texto)
    } finally {
      setBusy(false)
    }
  }

  const pracasParaRedirecionar = destinosDoRedirecionamento(locais, s.pracaId)
  const impRedirecionar = impedimentoRedirecionar(localDestinoRedir, motivoRedir)
  const bloqueioDoRedirecionamento = s.redirecionamento && !s.redirecionamento.pode && s.redirecionamento.impedimento !== 'REDIRECIONAMENTO_STATUS_INVALIDO'
    ? ERRO_TEXTO[s.redirecionamento.impedimento ?? ''] ?? null
    : null
  const redirecionar = async () => {
    const destino = pracasParaRedirecionar.find((l) => l.id === localDestinoRedir)
    if (!destino || impRedirecionar) return
    setBusy(true)
    try {
      await redirecionarEstoqueSolicitacao(s.id, destino.id, motivoRedir.trim())
      toast.success(`Pedido redirecionado para ${destino.nome}`)
      setLocalDestinoRedir(null)
      setMotivoRedir('')
      if (destino.noEscopo) await mutate()
      else navigate('/interno/estoque/fila')
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const descartarFotoSeparacao = (chave: string) => executar(async () => {
    await descartarEstoqueAnexo('SOLICITACAO', s.id, chave)
    await Promise.all([fotosQ.mutate(), mutate()])
  }, 'Foto descartada')

  const garantirContrato = () => executar(async () => {
    const r = await createEstoqueVinculo({ detentorLocalId: s.detentor_local_id, vigenciaFim: s.previsaoRetorno ?? s.previsao_retorno })
    const v = r?.data?.data
    if (v?.id) setExp((e) => ({ ...e, vinculoId: Number(v.id) }))
    await vinculosQ.mutate()
  }, 'Contrato do detentor garantido')

  /**
   * O que outra pessoa fez enquanto esta tela estava aberta.
   *
   * Expedir depende de duas coisas que podem vir de OUTRO operador: o contrato
   * (ESL11) e o comprovante (ESL18). Nenhuma das duas consultas revalida por
   * foco ou intervalo, então quem recebeu a orientação de "peça a quem tem a
   * permissão" ficava sem enxergar o resultado. Este botão é o caminho para
   * ver, sem recarregar a página.
   */
  const atualizarDependencias = async () => {
    setAtualizando(true)
    try {
      await Promise.all([vinculosQ.mutate(), anexosQ.mutate()])
    } finally {
      setAtualizando(false)
    }
  }

  const acoes = acoesDoPedido(s.status, temSaldo, motivo, cancelavel, faltaFoto)
  const emCampo = avisoEquipamentoEmCampo(s.status, s.remessasAbertas)
  const principal = acoes.principal?.para ?? null
  const prazo = prazoUi(s.prazoEm ?? s.prazo_em)
  const irParaExpedicao = () => document.getElementById('expedir')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const principalNoTrilho: AcaoDecisao | null = !atende ? null
    : acoes.principal
      ? { rotulo: acoes.principal.rotulo, rodar: () => transicionar(acoes.principal!.para), impedimento: acoes.principal.impedimento }
      : s.status === 'PRONTA' ? { rotulo: 'Ir para a expedição', rodar: irParaExpedicao } : null
  const confirmacaoDoCancelamento: AcaoDecisao[] = [
    { rotulo: 'Voltar', rodar: () => setConfirmandoCancelar(false), variante: 'secondary' as const },
    {
      rotulo: `Confirmar o cancelamento de ${s.numero}`,
      rodar: () => transicionar('CANCELADA'),
      impedimento: impConfirmarCancelamento,
      variante: 'secondary' as const,
    },
  ]
  const outrasNoTrilho: AcaoDecisao[] = !atende ? []
    : armado ? confirmacaoDoCancelamento
      : acoes.outras.map((a): AcaoDecisao => (a.para === 'CANCELADA'
        ? { rotulo: a.rotulo, rodar: () => setConfirmandoCancelar(true), variante: 'secondary' as const }
        : { rotulo: a.rotulo, rodar: () => transicionar(a.para), impedimento: a.impedimento }))
        .concat(bloqueioDoCancelamento
          ? [{ rotulo: ROTULO_TRANSICAO.CANCELADA, rodar: () => undefined, impedimento: bloqueioDoCancelamento, variante: 'secondary' as const }]
          : [])
  const aberto = !ehFinal(s.status)
  const telefone = numeroComDdi(campos.contatoTelefone as string | null | undefined)
  const juntarCampos = (chaves: string[]) => chaves.map((k) => campos[k])
    .filter((v) => v != null && String(v).trim() !== '').join(', ')
  const endereco = juntarCampos(['endereco', 'numero', 'complemento', 'cidade', 'uf', 'cep'])
  const enderecoMapa = juntarCampos(['endereco', 'numero', 'cidade', 'uf', 'cep'])
  const impedimento = impedimentoExpedicao(s, exp, { podeAnexar })
  const remessas = remessasQ.rows
  const valorCampo = (chave: string, v: any): string => (chave === 'contatoDocumento' ? exibirDocumento(String(v))
    : /^(data|prazo|janela)/.test(chave) ? fmtDate(String(v)) : String(v))
  const tituloItens = `Itens · ${t.linhasCobertas} de ${t.linhas} ${t.linhas === 1 ? 'item coberto' : 'itens cobertos'}`
    + ` · separado: ${textoRegime({ pecas: t.pecas.separada, consumo: t.consumo.separada })}`
    + (t.pecas.atendida + t.consumo.atendida > 0 ? ` · já entregues: ${textoRegime({ pecas: t.pecas.atendida, consumo: t.consumo.atendida })}` : '')
  // O bloco existe quando há o que mostrar OU o que consertar: fora da
  // primeira página, em erro ou carregando, ele precisa continuar visível.
  const mostrarRemessas = mostrarBlocoRemessas({
    linhas: remessas.length, page: pagRemessas, total: remessasQ.total, isLoading: remessasQ.isLoading, isError: remessasQ.isError,
  })

  const acoesItem = (l: LinhaCobertura) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {atende && emSeparacao && l.controle === 'QUANTIDADE' && (
        <>
          <Stepper
            valor={qtdSeparar[l.itemId] ?? String(l.separada)}
            aoMudar={(v) => setQtdSeparar({ ...qtdSeparar, [l.itemId]: v })}
            rotulo={`total separado de ${l.nome}`}
            max={limiteSeparacao(l)}
            disabled={busy}
          />
          <Button $variant="secondary" disabled={busy || qtdSeparar[l.itemId] == null || qtdSeparar[l.itemId] === ''} onClick={() => executar(() => separarQuantidadeEstoqueSolicitacao({ id: s.id, itemId: l.itemId, quantidade: Number(qtdSeparar[l.itemId]) }), 'Quantidade separada', () => setQtdSeparar({ ...qtdSeparar, [l.itemId]: '' }))}>Separar</Button>
        </>
      )}
      {atende && emSeparacao && l.controle === 'SERIAL' && (
        <Button $variant="secondary" disabled={busy} onClick={() => { setBuscaPeca(''); setPickerItem(pickerItem === l.itemId ? null : l.itemId) }}>{pickerItem === l.itemId ? 'Fechar' : 'Vincular peça'}</Button>
      )}
      {podeTransferir && rotaPedirTransferencia(s, l) && (
        <Button $variant="secondary" disabled={busy} onClick={() => navigate(rotaPedirTransferencia(s, l) as string)}>Pedir transferência</Button>
      )}
      {atende && podeAlterarQtd && (
        <>
          <Input type="number" min={0} style={{ width: 110 }} placeholder="nova qtd" value={qtdNova[l.itemId] ?? ''} onChange={(e) => setQtdNova({ ...qtdNova, [l.itemId]: e.target.value })} />
          <Button $variant="secondary" disabled={busy || !qtdNova[l.itemId]} onClick={() => executar(() => alterarQuantidadeEstoqueSolicitacao({ id: s.id, itemId: l.itemId, quantidade: Number(qtdNova[l.itemId]), motivo: motivoQtd.trim() || undefined }), 'Quantidade alterada', () => setQtdNova({ ...qtdNova, [l.itemId]: '' }))}>Alterar pedido</Button>
        </>
      )}
    </div>
  )

  const cartaoItem = (l: LinhaCobertura) => (
    <Card key={l.itemId} style={{ padding: 12, borderColor: l.falta > 0 && emSeparacao ? SEVERIDADE.atencao.borda : undefined }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b style={{ flexGrow: 1, fontSize: 15 }}>{l.nome}</b>
        <span style={{ fontSize: 12, color: BD.grayText }}>{l.controle === 'SERIAL' ? 'peça' : 'quantidade'}</span>
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: BD.grayText }}>
        {l.pedida} {l.pedida === 1 ? 'pedida' : 'pedidas'} · {l.separada} separada{l.separada === 1 ? '' : 's'}
        {l.atendida > 0 ? ` · ${l.atendida} entregue${l.atendida === 1 ? '' : 's'}` : ''}
        {l.falta > 0 && <span style={{ color: SEVERIDADE.atencao.texto, fontWeight: 600 }}> · falta {l.falta}</span>}
      </div>
      {(atende || podeTransferir) && <div style={{ marginTop: 10 }}>{acoesItem(l)}</div>}
    </Card>
  )

  const campoMotivo = () => (
    <Field><label>Motivo / mensagem {rotuloDoMotivo(s.status, temSaldo, manuais)}</label><Input value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} /></Field>
  )
  const campoVisivel = () => (
    <Field><label>Visível ao solicitante</label>
      <Select value={visivel ? '1' : '0'} onChange={(e) => setVisivel(e.target.value === '1')}><option value="1">sim</option><option value="0">não (nota interna)</option></Select>
    </Field>
  )
  const faltaNaSeparacao = emSeparacao && temSaldo
    ? `Ainda faltam ${textoRestante(rest)} para cobrir o pedido. Concluir a separação com falta abre a expedição parcial; encerrar o pedido assina que o restante não sai.`
    : null

  const blocoTransicoes = () => (
    <Bloco titulo="Mudar a situação do pedido">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {campoMotivo()}
        {campoVisivel()}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {armado ? (
          <>
            <Button $variant="secondary" disabled={busy} onClick={() => setConfirmandoCancelar(false)}>Voltar</Button>
            <Button $variant="secondary" disabled={busy || !!impConfirmarCancelamento} onClick={() => transicionar('CANCELADA')}>Confirmar o cancelamento de {s.numero}</Button>
          </>
        ) : manuais.map((p) => (
          <Button
            key={p}
            $variant={['REJEITADA', 'CANCELADA', 'CONCLUIDA'].indexOf(p) >= 0 ? 'secondary' : 'primary'}
            disabled={busy}
            onClick={() => (p === 'CANCELADA' ? setConfirmandoCancelar(true) : transicionar(p))}
          >
            {ROTULO_TRANSICAO[p]}
          </Button>
        ))}
      </div>
      {armado && impConfirmarCancelamento && <div style={{ fontSize: 13, color: BD.grayText, marginTop: 8 }}>{impConfirmarCancelamento}</div>}
      {!armado && bloqueioDoCancelamento && <div style={{ fontSize: 13, color: BD.grayText, marginTop: 8 }}>{bloqueioDoCancelamento}</div>}
      {faltaNaSeparacao && <div style={{ fontSize: 13, color: BD.grayText, marginTop: 8 }}>{faltaNaSeparacao}</div>}
    </Bloco>
  )

  return (
    <DetailLayout $contido>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{s.numero} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <SoDesktop>
            <PageSubtitle>
              {TIPO_DEMANDA_LABEL[s.tipo_demanda] ?? s.tipo_demanda} · {s.cliente_nome} · detentor {s.detentor_nome} · praça {s.pracaNome} · aberta por {s.solicitante_nome} em {fmtDateTime(s.criado_em)}
              {' · '}prazo {fmtDate(s.prazoEm ?? s.prazo_em)} · retorno previsto {fmtDate(s.previsaoRetorno ?? s.previsao_retorno)}
              {emCampo && <><br />{emCampo}</>}
            </PageSubtitle>
          </SoDesktop>
          <SoCelular>
            <PageSubtitle>
              {TIPO_DEMANDA_LABEL[s.tipo_demanda] ?? s.tipo_demanda} · {s.detentor_nome}
              <br />{s.pracaNome} · pediu {s.solicitante_nome}
              {emCampo && <><br />{emCampo}</>}
            </PageSubtitle>
          </SoCelular>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/fila')}>‹ Fila</Button>
      </div>

      {!atende && <Card style={{ padding: 12, marginBottom: 16, color: BD.grayText }}>Você visualiza este pedido; agir sobre ele exige a permissão "Atender solicitação".</Card>}

      <SoCelular>
        {!ehFinal(s.status) && (
          <Card style={{ padding: 12, marginBottom: 16, borderColor: prazo.alerta ? SEVERIDADE.atencao.borda : undefined, background: prazo.alerta ? SEVERIDADE.atencao.bg : undefined }}>
            <div style={{ fontWeight: 600, color: prazo.alerta ? SEVERIDADE.atencao.texto : BD.black }}>{prazo.texto}</div>
            <div style={{ fontSize: 12, color: BD.grayText }}>Retorno previsto: {fmtDate(s.previsaoRetorno ?? s.previsao_retorno)}</div>
          </Card>
        )}
        {(telefone || endereco) && (
          <Card style={{ padding: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: BD.grayText }}>Local e contato</div>
            {endereco && <div style={{ marginTop: 4, fontSize: 14 }}>{endereco}</div>}
            {campos.pontoReferencia && <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>Referência: {String(campos.pontoReferencia)}</div>}
            {campos.contatoNome && <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>Contato: {String(campos.contatoNome)}{campos.contatoDocumento ? ` · ${exibirDocumento(String(campos.contatoDocumento))}` : ''}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {telefone && <LinkBotao href={`tel:+${telefone}`}>Ligar</LinkBotao>}
              {telefone && <LinkBotao href={`https://wa.me/${telefone}`} target="_blank" rel="noreferrer">WhatsApp</LinkBotao>}
              {enderecoMapa && <LinkBotao href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoMapa)}`} target="_blank" rel="noreferrer">Rotas</LinkBotao>}
            </div>
          </Card>
        )}
      </SoCelular>

      {s.status === 'AGUARDANDO_INFO' && (
        <Bloco titulo="Aguardando o solicitante" destaque>
          <div>{s.historico.find((h) => h.para === 'AGUARDANDO_INFO')?.mensagem ?? '—'}</div>
        </Bloco>
      )}
      {s.motivo_recusa && <Bloco titulo="Motivo da recusa">{s.motivo_recusa}</Bloco>}
      {s.destino_saldo && <Bloco titulo="Destino do saldo">{DESTINOS_SALDO.find((d) => d.destino === s.destino_saldo)?.rotulo ?? s.destino_saldo}{s.motivo_saldo ? ` · ${s.motivo_saldo}` : ''}</Bloco>}

      {atende && s.status === 'EM_ANALISE' && (
        <Bloco titulo="Pedir informação ao solicitante">
          <Field><label>Pergunta</label><Input value={pergunta} maxLength={500} onChange={(e) => setPergunta(e.target.value)} /></Field>
          <Button $variant="secondary" disabled={busy || !pergunta.trim()} style={{ marginTop: 8 }} onClick={() => executar(() => pedirInformacaoEstoqueSolicitacao(s.id, pergunta.trim()), 'Pergunta enviada; o prazo pausa até a resposta', () => setPergunta(''))}>Pedir informação</Button>
        </Bloco>
      )}

      {atende && (s.redirecionamento?.pode || bloqueioDoRedirecionamento) && (
        <Bloco titulo="Redirecionar para outra praça">
          {bloqueioDoRedirecionamento ? (
            <div style={{ fontSize: 13, color: BD.grayText }}>{bloqueioDoRedirecionamento}</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
                A praça escolhida passa a atender o pedido, que volta para análise. A logística dela é avisada e você deixa de ser o responsável.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <Field>
                  <label>Praça que vai atender</label>
                  <Select value={localDestinoRedir ?? ''} onChange={(e) => setLocalDestinoRedir(Number(e.target.value) || null)}>
                    <option value="">— escolha —</option>
                    {pracasParaRedirecionar.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </Select>
                </Field>
                <Field><label>Motivo</label><Input value={motivoRedir} maxLength={500} onChange={(e) => setMotivoRedir(e.target.value)} /></Field>
              </div>
              <Button $variant="secondary" disabled={busy || !!impRedirecionar} style={{ marginTop: 8 }} onClick={redirecionar}>Redirecionar</Button>
            </>
          )}
        </Bloco>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        <Bloco titulo="Eventos">
          <Table><thead><tr><th>Evento</th><th>Data</th></tr></thead>
            <tbody>{s.eventos.map((e) => <tr key={e.eventoId}><td>{e.eventoNome}</td><td>{fmtDate(e.eventoData)}</td></tr>)}</tbody></Table>
        </Bloco>
        <Bloco titulo="Dados da demanda">
          {Object.keys(campos).length === 0 && <div style={{ color: BD.grayText }}>Sem dados adicionais.</div>}
          {Object.entries(campos).map(([k, v]) => (
            <div key={k} style={{ fontSize: 13 }}><span style={{ color: BD.grayText }}>{ROTULO_CAMPO[k] ?? k}:</span> {valorCampo(k, v)}</div>
          ))}
          {s.observacao && <div style={{ fontSize: 13, marginTop: 8 }}><span style={{ color: BD.grayText }}>Observação:</span> {s.observacao}</div>}
        </Bloco>
      </div>

      <Bloco titulo={tituloItens}>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Pedido</th><th style={{ textAlign: 'right' }}>Entregue</th><th style={{ textAlign: 'right' }}>Separado</th><th style={{ textAlign: 'right' }}>Falta</th>{(atende || podeTransferir) && <th>Ações</th>}</tr></thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.itemId} style={{ background: l.falta > 0 && emSeparacao ? SEVERIDADE.atencao.bg : undefined }}>
                  <td>{l.nome} <span style={{ color: BD.grayText, fontSize: 12 }}>{l.controle === 'SERIAL' ? 'peça' : 'quantidade'}</span></td>
                  <td style={{ textAlign: 'right' }}>{l.pedida}</td>
                  <td style={{ textAlign: 'right' }}>{l.atendida}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{l.separada}</td>
                  <td style={{ textAlign: 'right' }}>{l.falta}</td>
                  {(atende || podeTransferir) && (
                    <td>
                      {acoesItem(l)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>{linhas.map(cartaoItem)}</ListaCartoes>

        {atende && podeAlterarQtd && (
          <Field style={{ marginTop: 8 }}><label>Motivo da alteração de quantidade (opcional)</label><Input value={motivoQtd} maxLength={400} onChange={(e) => setMotivoQtd(e.target.value)} /></Field>
        )}
        {atende && emSeparacao && pickerItem && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 4 }}>Peças disponíveis nesta praça para {linhas.find((l) => l.itemId === pickerItem)?.nome}:</div>
            <SeletorDePecas
              consulta={disponiveisQ}
              busca={buscaPeca}
              ocultas={s.unidades.map((u) => u.unidadeId)}
              aoBuscar={setBuscaPeca}
              aoEscolher={async (u) => {
                await executar(() => vincularEstoqueSeparacao({ id: s.id, unidadeId: u.id }), `Peça ${u.serial} vinculada`)
                disponiveisQ.mutate()
              }}
              disabled={busy}
              mostrarPatrimonio
              vazio={<span style={{ color: BD.red, fontSize: 13 }}>Nenhuma peça disponível. Pedir transferência é o passo seguinte (Aguardar transferência).</span>}
            />
          </div>
        )}
        {s.unidades.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Peças vinculadas a esta separação</div>
            <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead><tr><th>Item</th><th>Serial</th><th>Patrimônio</th><th>Vinculada em</th>{atende && emSeparacao && <th />}</tr></thead>
              <tbody>
                {s.unidades.map((u) => (
                  <tr key={u.unidadeId}>
                    <td>{u.itemNome}</td><td style={{ fontFamily: 'monospace' }}>{u.serial}</td><td>{u.patrimonio ?? '—'}</td><td style={{ fontSize: 13 }}>{fmtDateTime(u.vinculadoEm)}</td>
                    {atende && emSeparacao && <td><Button $variant="secondary" disabled={busy} onClick={() => executar(() => desvincularEstoqueSeparacao({ id: s.id, unidadeId: u.unidadeId }), 'Peça desvinculada')}>Desvincular</Button></td>}
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          </div>
        )}
        {((atende && rodadaAberta) || fotosQ.data.length > 0) && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${BD.grayBorder}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Foto da separação</div>
            <div style={{ fontSize: 12, color: faltaFoto ? SEVERIDADE.atencao.texto : BD.grayText, marginBottom: 8 }}>
              {s.separacao?.fotoObrigatoria
                ? `Obrigatória para concluir a separação: ${Number(s.separacao.fotosDaRodada) === 1 ? '1 foto desta rodada' : `${Number(s.separacao.fotosDaRodada)} fotos desta rodada`}.`
                : 'A foto dos equipamentos separados é a contraprova quando o equipamento volta com avaria.'}
            </div>
            {atende && rodadaAberta && (podeAnexar
              ? <AnexarArquivo aoEnviar={enviarFotoSeparacao} disabled={busy} aceita="image/*" />
              : <div style={{ fontSize: 13, color: BD.grayText }}>Enviar a foto exige a permissão "Anexos". Peça a quem a tem, ou peça a permissão.</div>)}
            {fotosQ.isError && <div style={{ fontSize: 13, color: BD.red, marginTop: 6 }}>Não foi possível carregar as fotos. <Button $variant="secondary" onClick={() => fotosQ.mutate()}>Tentar de novo</Button></div>}
            {fotos.daRodada.length > 0 && (
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {fotos.daRodada.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                    <a href={a.url} target="_blank" rel="noreferrer">{a.nome}</a>
                    <span style={{ color: BD.grayText }}>{a.enviadoPorNome} · {fmtDateTime(a.enviadoEm)}</span>
                    {atende && rodadaAberta && podeAnexar && usuarioId != null && Number(a.enviadoPorId) === usuarioId && (
                      <Button $variant="secondary" disabled={busy} onClick={() => descartarFotoSeparacao(a.chave)}>Descartar</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {fotos.anteriores.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: BD.grayText }}>
                Rodadas anteriores:{' '}
                {fotos.anteriores.map((a, k) => (
                  <span key={a.id}>{k > 0 ? ' · ' : ''}<a href={a.url} target="_blank" rel="noreferrer">{a.nome}</a> ({fmtDateTime(a.enviadoEm)})</span>
                ))}
              </div>
            )}
          </div>
        )}
      </Bloco>

      {atende && s.status === 'PRONTA' && (
        <Bloco titulo="Expedir" id="expedir">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <Button $variant="secondary" disabled={atualizando} onClick={atualizarDependencias}>
              {atualizando ? 'Atualizando…' : 'Atualizar contrato e comprovantes'}
            </Button>
            <span style={{ fontSize: 12, color: BD.grayText }}>
              O contrato (ESL11) e o comprovante (ESL18) podem vir de outra pessoa. Esta tela não os recarrega sozinha.
            </span>
            {(vinculosQ.isLoading || anexosQ.isLoading) && <Spinner />}
            {(vinculosQ.isError || anexosQ.isError) && (
              <span style={{ fontSize: 13, color: BD.red }}>
                {vinculosQ.isError && anexosQ.isError ? 'Não foi possível carregar contrato e comprovantes.'
                  : vinculosQ.isError ? 'Não foi possível carregar os contratos do detentor.'
                    : 'Não foi possível carregar os comprovantes já enviados.'}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <Field>
              <label>Contrato do detentor</label>
              {vinculos.length ? (
                <Select value={exp.vinculoId ?? ''} onChange={(e) => setExp({ ...exp, vinculoId: Number(e.target.value) || null })}>
                  {vinculos.map((v) => {
                    const aviso = avisoDoVinculoNaExpedicao(v.vigenciaFim, hoje, s.previsaoRetorno ?? s.previsao_retorno)
                    return <option key={v.id} value={v.id}>{v.codigo} · até {fmtDate(v.vigenciaFim)}{v.vigenciaSugerida ? ' (sugerida)' : ''}{aviso ? ` · ${aviso}` : ''}</option>
                  })}
                </Select>
              ) : vinculosQ.isLoading ? (
                <div role="status" style={{ fontSize: 13, color: BD.grayText }}>Carregando os contratos do detentor…</div>
              ) : vinculosQ.isError ? (
                <div style={{ fontSize: 13, color: BD.grayText }}>Os contratos não carregaram. Use "Atualizar contrato e comprovantes" antes de garantir um contrato.</div>
              ) : podeVincular ? (
                <Button $variant="secondary" disabled={busy || atualizando || vinculosQ.isError || !s.detentor_local_id} onClick={garantirContrato}>Garantir contrato</Button>
              ) : (
                <div style={{ fontSize: 13, color: BD.grayText }}>
                  Este detentor ainda não tem contrato vigente, e criar um exige a permissão "Vínculos (contratos e termos)". Peça a quem a tem para criar o contrato deste detentor.
                </div>
              )}
            </Field>
            <Field>
              <label>Método de envio</label>
              <Select value={exp.metodoEnvio} onChange={(e) => setExp({ ...exp, metodoEnvio: e.target.value as MetodoEnvio | '' })}>
                <option value="">— escolha —</option>
                {METODOS_ENVIO.map((m) => <option key={m.metodo} value={m.metodo}>{m.rotulo}</option>)}
              </Select>
              {exp.metodoEnvio && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>{METODOS_ENVIO.find((m) => m.metodo === exp.metodoEnvio)?.comprovante}</div>}
            </Field>
            {exp.metodoEnvio === 'TRANSPORTADORA' && (
              <>
                <Field><label>Nome da transportadora</label><Input value={exp.transportadora} maxLength={120} onChange={(e) => setExp({ ...exp, transportadora: e.target.value })} /></Field>
                <Field><label>Código de rastreio (opcional)</label><Input value={exp.codigoRastreio} maxLength={80} onChange={(e) => setExp({ ...exp, codigoRastreio: e.target.value })} /></Field>
              </>
            )}
            {exp.metodoEnvio === 'EQUIPE_PROPRIA' && (
              <>
                <Field><label>Quem recebeu (opcional)</label><Input value={exp.recebidoPor} maxLength={150} onChange={(e) => setExp({ ...exp, recebidoPor: e.target.value })} /></Field>
                <Field><label>CPF ou CNPJ de quem recebeu (opcional)</label><Input value={exp.recebidoDocumento} maxLength={18} onChange={(e) => setExp({ ...exp, recebidoDocumento: mascaraDocumento(e.target.value) })} /></Field>
              </>
            )}
            <Field>
              <label>Comprovante de despacho</label>
              {exp.comprovanteKey ? (
                <div style={{ fontSize: 13 }}>{comprovanteNome} <Button $variant="secondary" disabled={busy} onClick={trocarComprovante}>Trocar</Button></div>
              ) : (
                <>
                  {podeAnexar ? (
                    <AnexarArquivo aoEnviar={enviarComprovante} disabled={busy} />
                  ) : (
                    <div style={{ fontSize: 13, color: BD.grayText }}>
                      Enviar exige a permissão "Anexos". {comprovantesSalvos.length ? 'Use um comprovante já enviado:' : 'Peça a quem tem a permissão para enviar o comprovante deste pedido.'}
                    </div>
                  )}
                  {comprovantesSalvos.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <Select value="" disabled={busy} onChange={(e) => {
                        const a = comprovantesSalvos.find((x) => x.chave === e.target.value)
                        if (a) { setExp({ ...exp, comprovanteKey: a.chave }); setComprovanteNome(a.nome) }
                      }}>
                        <option value="">— comprovante já enviado —</option>
                        {comprovantesSalvos.map((a) => <option key={a.id} value={a.chave}>{a.nome} · {a.enviadoPorNome}</option>)}
                      </Select>
                    </div>
                  )}
                </>
              )}
            </Field>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Eventos que esta remessa atende</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {s.eventos.map((ev) => {
                const on = exp.eventos.indexOf(ev.eventoId) >= 0
                return <Button key={ev.eventoId} $variant={on ? 'primary' : 'secondary'} onClick={() => setExp({ ...exp, eventos: on ? exp.eventos.filter((x) => x !== ev.eventoId) : [...exp.eventos, ev.eventoId] })}>{on ? '✓ ' : ''}{ev.eventoNome} <span style={{ opacity: 0.7 }}>{fmtDate(ev.eventoData)}</span></Button>
              })}
            </div>
          </div>
          {rest.total > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Expedição parcial: faltam {textoRestante(rest)}. O que acontece com o saldo?</div>
              {DESTINOS_SALDO.map((d) => (
                <label key={d.destino} style={{ display: 'block', fontSize: 13, marginBottom: 4, cursor: 'pointer' }}>
                  <input type="radio" name="destinoSaldo" checked={exp.destinoSaldo === d.destino} onChange={() => setExp({ ...exp, destinoSaldo: d.destino as DestinoSaldo })} /> {d.rotulo} <span style={{ color: BD.grayText }}>— {d.efeito}</span>
                </label>
              ))}
              {exp.destinoSaldo === 'CANCELAR_SALDO' && <Field><label>Motivo</label><Input value={exp.motivoSaldo} maxLength={400} onChange={(e) => setExp({ ...exp, motivoSaldo: e.target.value })} /></Field>}
            </div>
          )}
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button disabled={busy || !!impedimento} onClick={() => executar(() => expedirEstoqueSolicitacao({
              id: s.id, vinculoId: exp.vinculoId, metodoEnvio: exp.metodoEnvio, comprovanteDespachoUrl: exp.comprovanteKey,
              transportadora: exp.metodoEnvio === 'TRANSPORTADORA' ? exp.transportadora.trim() : null, eventos: exp.eventos,
              codigoRastreio: exp.metodoEnvio === 'TRANSPORTADORA' ? exp.codigoRastreio.trim() || null : null,
              recebidoPor: exp.metodoEnvio === 'EQUIPE_PROPRIA' ? exp.recebidoPor.trim() || null : null,
              recebidoDocumento: exp.metodoEnvio === 'EQUIPE_PROPRIA' ? exp.recebidoDocumento.trim() || null : null,
              destinoSaldo: rest.total > 0 ? exp.destinoSaldo || null : null, motivoSaldo: exp.destinoSaldo === 'CANCELAR_SALDO' ? exp.motivoSaldo.trim() : null,
            }), 'Remessa expedida')}>Expedir</Button>
            {impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{impedimento}</span>}
          </div>
        </Bloco>
      )}

      {mostrarRemessas && (
        // A rota devolve 25 por página. O bloco NÃO pode depender de haver
        // linhas nesta página: a página 2 que falha esconderia junto os
        // controles de voltar, e o pedido pareceria sem remessa nenhuma.
        <Bloco titulo={`Remessas deste pedido${remessasQ.isError ? '' : ` · ${remessasQ.total}`}`}>
          {remessasQ.isLoading ? <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div> : (
            <>
              {remessasQ.isError ? (
                <div style={{ color: BD.red, fontSize: 13 }}>
                  Não foi possível carregar as remessas deste pedido.{' '}
                  <Button $variant="secondary" onClick={() => remessasQ.mutate()}>Tentar de novo</Button>
                </div>
              ) : remessas.length === 0 ? (
                <div style={{ color: BD.grayText, fontSize: 13 }}>Nenhuma remessa nesta página.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead><tr><th>Código</th><th>Status</th><th>Envio</th><th>Despachada</th><th>Entregue</th><th>Retorno previsto</th></tr></thead>
                  <tbody>
                    {remessas.map((r) => (
                      <tr key={r.id}>
                        <td style={{ fontFamily: 'monospace' }}>
                          <a href={`/interno/estoque/remessas/${r.id}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/remessas/${r.id}`) }}>{r.codigo}</a>
                        </td>
                        <td>{STATUS_REMESSA_UI[r.status as StatusRemessa]?.label ?? r.status}</td>
                        <td>{rotuloMetodoEnvio(r.metodoEnvio)}{r.transportadora ? ` · ${r.transportadora}` : ''}{r.codigoRastreio ? ` · rastreio ${r.codigoRastreio}` : ''}</td>
                        <td>{fmtDateTime(r.despachadoEm)}</td>
                        <td>{fmtDate(r.entregueEm)}</td>
                        <td>{fmtDate(r.prorrogadoPara ?? r.previsaoRetorno)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </div>
              )}
              {(pagRemessas > 1 || !remessasQ.lastPage) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 12 }}>
                  <Button $variant="secondary" disabled={pagRemessas <= 1} onClick={() => setPagRemessas((p) => p - 1)}>‹ Anterior</Button>
                  <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagRemessas}</span>
                  <Button $variant="secondary" disabled={remessasQ.isError || !!remessasQ.lastPage} onClick={() => setPagRemessas((p) => p + 1)}>Próxima ›</Button>
                </div>
              )}
            </>
          )}
        </Bloco>
      )}

      {(s.transferencias ?? []).length > 0 && (
        <Bloco titulo={`Transferências deste pedido · ${(s.transferencias ?? []).length}`}>
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            {(s.transferencias ?? []).map((t) => {
              const tu = STATUS_TRANSFERENCIA_UI[t.status]
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <a href={`/interno/estoque/transferencias/${t.id}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/transferencias/${t.id}`) }} style={{ fontFamily: 'monospace' }}>{t.numero}</a>
                  {tu ? <StatusBadge $bg={tu.bg} $color={tu.color}>{tu.label}</StatusBadge> : t.status}
                  <span style={{ color: BD.grayText }}>de {t.origemNome} · {t.ladoSolicitante === 'DESTINO' ? 'pedida por esta praça' : 'oferecida pela origem'} · {fmtDateTime(t.criadoEm)}</span>
                </div>
              )
            })}
          </div>
        </Bloco>
      )}

      <Bloco titulo="Histórico">
        <TabelaWrap>
          <Table>
            <thead><tr><th>Quando</th><th>Quem</th><th>Situação</th><th>Mensagem</th><th>Visível</th></tr></thead>
            <tbody>
              {s.historico.map((h, k) => {
                const hu = h.tipo === 'NOTA' ? NOTA_UI : h.tipo === 'REDIRECIONAMENTO' ? REDIRECIONAMENTO_UI : STATUS_SOLICITACAO_UI[h.para]
                return <tr key={k}><td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(h.criadoEm)}</td><td style={{ fontSize: 13 }}>{h.usuarioNome}</td><td>{hu ? <StatusBadge $bg={hu.bg} $color={hu.color}>{hu.label}</StatusBadge> : h.para}</td><td>{h.mensagem ?? ''}</td><td style={{ fontSize: 12, color: BD.grayText }}>{Number(h.visivelSolicitante) ? 'solicitante' : 'interna'}</td></tr>
              })}
            </tbody>
          </Table>
        </TabelaWrap>
        <ListaCartoes>
          {s.historico.map((h, k) => {
            const hu = h.tipo === 'NOTA' ? NOTA_UI : h.tipo === 'REDIRECIONAMENTO' ? REDIRECIONAMENTO_UI : STATUS_SOLICITACAO_UI[h.para]
            return (
              <Card key={k} style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {hu ? <StatusBadge $bg={hu.bg} $color={hu.color}>{hu.label}</StatusBadge> : <b>{h.para}</b>}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: BD.grayText }}>{fmtDateTime(h.criadoEm)}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13 }}>{h.mensagem ?? ''}</div>
                <div style={{ marginTop: 4, fontSize: 12, color: BD.grayText }}>
                  {h.usuarioNome} · {Number(h.visivelSolicitante) ? 'visível ao solicitante' : 'nota interna'}
                </div>
              </Card>
            )
          })}
        </ListaCartoes>
      </Bloco>

      {atende && (principal || s.status === 'PRONTA') && (
        <>
          <BarraFixa>
            <div style={{ display: 'flex', gap: 10 }}>
              {manuais.length > 1 && (
                <BotaoIcone type="button" aria-label="Outras situações do pedido" onClick={() => setAcoesAbertas(true)}>⋮</BotaoIcone>
              )}
              {principal ? (
                <Button
                  style={{ flexGrow: 1 }}
                  disabled={busy}
                  onClick={() => (exigeMotivo(s.status, principal, temSaldo) ? setAcoesAbertas(true) : transicionar(principal))}
                >
                  {ROTULO_TRANSICAO[principal]}
                </Button>
              ) : (
                <LinkBotao href="#expedir" $primario>Ir para a expedição</LinkBotao>
              )}
            </div>
          </BarraFixa>
        </>
      )}

      {acoesAbertas && (
        <>
          <FolhaOverlay $open onClick={fecharFolhaDeAcoes} />
          <Folha $open role="dialog" aria-label="Situação do pedido">
            <FolhaPega />
            <FolhaTopo>
              <h2>Situação do pedido</h2>
              <Button $variant="secondary" onClick={fecharFolhaDeAcoes}>Fechar</Button>
            </FolhaTopo>
            {blocoTransicoes()}
          </Folha>
        </>
      )}
      </div>

      <Decisao
        estado={ui}
        desde={aberto && !prazo.alerta ? prazo.texto : null}
        aviso={aberto && prazo.alerta ? prazo.texto : null}
        formulario={atende && manuais.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {campoMotivo()}
            {campoVisivel()}
          </div>
        ) : null}
        principal={principalNoTrilho}
        secundarias={outrasNoTrilho}
        pendencia={atende ? faltaNaSeparacao : null}
        somenteTrilho
        busy={busy}
      />
    </DetailLayout>
  )
}

export default EstoquePedido
